import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';

// ============================================================================
// TYPES
// ============================================================================

interface AccountInfo {
  id: string;
  account_name: string;
  username?: string;
  status: string;
  is_active: number;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  platform_provider: string | null;
  sync_frequency: string;
  network?: { name: string };
  parent_network?: { name: string } | null;
  summary?: {
    months_synced: number;
    total_commission: number;
    total_ngr: number;
    has_data: boolean;
  };
}

interface SyncResult {
  accountId: string;
  accountName: string;
  platform: string | null;
  status: 'pending' | 'syncing' | 'success' | 'failed' | 'skipped';
  error?: string;
  duration?: number;
  response?: any;
  monthsSynced?: number;
  timestamp?: string;
}

type LogLevel = 'info' | 'success' | 'error' | 'warn' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  accountName?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function SyncAll() {
  const { token } = useAuthStore();
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [results, setResults] = useState<Map<string, SyncResult>>(new Map());
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [abortRequested, setAbortRequested] = useState(false);
  const [filterPlatform, setFilterPlatform] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [forceResync, setForceResync] = useState(false);
  const [syncStartTime, setSyncStartTime] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const abortRef = useRef(false);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ============================================================================
  // LOGGING
  // ============================================================================

  const log = useCallback((level: LogLevel, message: string, accountName?: string) => {
    const entry: LogEntry = {
      timestamp: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      level,
      message,
      accountName,
    };
    setLogs(prev => [...prev, entry]);
  }, []);

  // Auto-scroll log to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Elapsed timer
  useEffect(() => {
    if (syncing && syncStartTime) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - syncStartTime) / 1000));
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [syncing, syncStartTime]);

  // ============================================================================
  // LOAD ACCOUNTS
  // ============================================================================

  const loadAccounts = async () => {
    setLoading(true);
    log('info', 'Loading all accounts...');
    try {
      const resp = await fetch('/api/networks', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json() as { success: boolean; data?: AccountInfo[]; error?: string };

      if (!data.success || !data.data) {
        log('error', `Failed to load accounts: ${data.error || 'Unknown error'}`);
        return;
      }

      // Only active accounts
      const activeAccounts = data.data.filter(a => a.is_active === 1);
      setAccounts(activeAccounts);

      // Count by platform
      const platformCounts: Record<string, number> = {};
      for (const a of activeAccounts) {
        const p = a.platform_provider || 'Unknown';
        platformCounts[p] = (platformCounts[p] || 0) + 1;
      }

      log('success', `Loaded ${activeAccounts.length} active accounts`);
      log('info', `Platforms: ${Object.entries(platformCounts).map(([k, v]) => `${k}: ${v}`).join(', ')}`);

      // Log accounts with issues
      const neverSynced = activeAccounts.filter(a => !a.last_sync_at);
      const lastFailed = activeAccounts.filter(a => a.last_sync_status === 'failed');
      const noData = activeAccounts.filter(a => !a.summary?.has_data);

      if (neverSynced.length > 0) {
        log('warn', `${neverSynced.length} accounts have never been synced`);
      }
      if (lastFailed.length > 0) {
        log('warn', `${lastFailed.length} accounts have last_sync_status = 'failed'`);
      }
      if (noData.length > 0) {
        log('warn', `${noData.length} accounts have no data in statistics_monthly`);
      }
    } catch (err: any) {
      log('error', `Failed to load accounts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  // ============================================================================
  // SYNC ALL
  // ============================================================================

  const syncAllAccounts = async () => {
    const accountsToSync = getFilteredAccounts();
    if (accountsToSync.length === 0) {
      log('warn', 'No accounts match the current filters');
      return;
    }

    if (forceResync) {
      const confirmed = window.confirm(
        `Force resync ${accountsToSync.length} accounts? This will re-fetch ALL historical data (from 2019) for each account. This can take a very long time.`
      );
      if (!confirmed) return;
    }

    setSyncing(true);
    setAbortRequested(false);
    abortRef.current = false;
    const startTime = Date.now();
    setSyncStartTime(startTime);
    setElapsed(0);

    // Initialize results
    const initialResults = new Map<string, SyncResult>();
    for (const account of accountsToSync) {
      initialResults.set(account.id, {
        accountId: account.id,
        accountName: account.account_name,
        platform: account.platform_provider,
        status: 'pending',
      });
    }
    setResults(initialResults);

    log('info', '='.repeat(60));
    log('info', `${forceResync ? 'FORCE RESYNC' : 'SYNC ALL'} STARTED - ${accountsToSync.length} accounts${forceResync ? ' (all historical data)' : ''}`);
    log('info', `Current month: ${new Date().toISOString().slice(0, 7)}`);
    log('info', '='.repeat(60));

    let successCount = 0;
    let failCount = 0;
    let skipCount = 0;

    for (let i = 0; i < accountsToSync.length; i++) {
      if (abortRef.current) {
        log('warn', `ABORTED by user after ${i} accounts`);
        // Mark remaining as skipped
        for (let j = i; j < accountsToSync.length; j++) {
          setResults(prev => {
            const next = new Map(prev);
            next.set(accountsToSync[j].id, {
              ...next.get(accountsToSync[j].id)!,
              status: 'skipped',
            });
            return next;
          });
          skipCount++;
        }
        break;
      }

      const account = accountsToSync[i];
      const accountLabel = `[${i + 1}/${accountsToSync.length}] ${account.account_name}`;

      log('info', `${accountLabel} - Starting sync...`, account.account_name);
      log('debug', `  Platform: ${account.platform_provider || 'Unknown'} | Last sync: ${account.last_sync_at || 'Never'} | Status: ${account.last_sync_status || 'N/A'}`, account.account_name);

      // Mark as syncing
      setResults(prev => {
        const next = new Map(prev);
        next.set(account.id, {
          ...next.get(account.id)!,
          status: 'syncing',
        });
        return next;
      });

      const syncStart = Date.now();

      try {
        // Call historical-sync — optionally with force=true for full resync
        // Force resync paginates: 6 months per request, loop until done
        let currentOffset = 0;
        let totalMonthsSynced = 0;
        let totalRecordsInserted = 0;
        let iterations = 0;
        const MAX_ITERATIONS = 20;
        let lastResponseData: any = null;
        let syncFailed = false;

        while (iterations < MAX_ITERATIONS) {
          iterations++;
          if (abortRef.current) break;

          const syncParams = new URLSearchParams({ skip_players: 'true' });
          if (forceResync) {
            syncParams.append('force', 'true');
            if (currentOffset > 0) syncParams.append('offset', currentOffset.toString());
          }

          const resp = await fetch(`/api/networks/${account.id}/historical-sync?${syncParams.toString()}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          const responseText = await resp.text();
          let responseData: any;
          try {
            responseData = JSON.parse(responseText);
          } catch {
            responseData = { raw: responseText.substring(0, 500) };
          }
          lastResponseData = responseData;

          if (!resp.ok || !responseData.success) {
            syncFailed = true;
            break;
          }

          const batchMonths = responseData.summary?.months_synced_now || 0;
          const batchRecords = responseData.summary?.total_records_inserted || 0;
          totalMonthsSynced += batchMonths;
          totalRecordsInserted += batchRecords;

          // Log batch progress for force resync
          if (forceResync && responseData.summary?.has_more_months) {
            log('info', `${accountLabel} - batch ${iterations}: ${batchMonths} months synced (${responseData.summary.remaining_months} remaining)`, account.account_name);
          }

          // Log month-by-month details
          if (responseData.details?.length) {
            for (const m of responseData.details) {
              if (m.status === 'error' || m.status === 'exception') {
                log('warn', `  Month ${m.month}: ERROR - ${m.error}`, account.account_name);
              } else if (m.status === 'success') {
                const earnings = m.earnings;
                const earningsStr = earnings ? ` | Commission: ${Number(earnings.total || 0).toFixed(2)}, CPA: ${Number(earnings.cpa || 0).toFixed(2)}, RevShare: ${Number(earnings.revenueShare || 0).toFixed(2)}` : '';
                log('debug', `  Month ${m.month}: ${m.records} records (${m.type || 'monthly'})${earningsStr}`, account.account_name);
              }
            }
          }

          // Check if there are more months to process
          if (!forceResync || !responseData.summary?.has_more_months) break;
          currentOffset = responseData.summary.next_offset || (currentOffset + 6);
        }

        const duration = Date.now() - syncStart;

        if (!syncFailed && lastResponseData?.success) {
          log('success', `${accountLabel} - OK (${duration}ms) - ${totalMonthsSynced} months synced, ${totalRecordsInserted} records inserted${forceResync ? ` (${iterations} batches)` : ''}`, account.account_name);

          // Log any warnings from the last response
          if (lastResponseData.warnings?.length) {
            for (const w of lastResponseData.warnings) {
              log('warn', `  Warning: ${w}`, account.account_name);
            }
          }

          setResults(prev => {
            const next = new Map(prev);
            next.set(account.id, {
              ...next.get(account.id)!,
              status: 'success',
              duration,
              response: lastResponseData,
              monthsSynced: totalMonthsSynced,
              timestamp: new Date().toISOString(),
            });
            return next;
          });
          successCount++;
        } else {
          const errorMsg = lastResponseData?.error || lastResponseData?.message || 'Sync failed';
          log('error', `${accountLabel} - FAILED (${duration}ms) - ${errorMsg}`, account.account_name);

          // Log detailed error info
          if (lastResponseData?.debug) {
            log('debug', `  Debug: ${JSON.stringify(lastResponseData.debug).substring(0, 300)}`, account.account_name);
          }
          if (lastResponseData?.stack) {
            log('debug', `  Stack: ${lastResponseData.stack.substring(0, 200)}`, account.account_name);
          }

          setResults(prev => {
            const next = new Map(prev);
            next.set(account.id, {
              ...next.get(account.id)!,
              status: 'failed',
              error: errorMsg,
              duration,
              response: lastResponseData,
              timestamp: new Date().toISOString(),
            });
            return next;
          });
          failCount++;
        }
      } catch (err: any) {
        const duration = Date.now() - syncStart;
        log('error', `${accountLabel} - EXCEPTION (${duration}ms) - ${err.message}`, account.account_name);

        setResults(prev => {
          const next = new Map(prev);
          next.set(account.id, {
            ...next.get(account.id)!,
            status: 'failed',
            error: err.message,
            duration,
            timestamp: new Date().toISOString(),
          });
          return next;
        });
        failCount++;
      }

      // Rate limit delay between accounts (2s)
      if (i < accountsToSync.length - 1 && !abortRef.current) {
        await new Promise(r => setTimeout(r, 2000));
      }
    }

    const totalDuration = Math.floor((Date.now() - startTime) / 1000);

    log('info', '='.repeat(60));
    log('info', `SYNC ALL COMPLETE - ${totalDuration}s total`);
    log('success', `Success: ${successCount} | Failed: ${failCount} | Skipped: ${skipCount}`);
    log('info', '='.repeat(60));

    // Log failed accounts summary
    const failedAccounts = accountsToSync.filter(a => {
      const r = results.get(a.id);
      // Can't rely on state here due to closure, recalculate
      return false; // Will be visible in the results table
    });

    setSyncing(false);
    setSyncStartTime(null);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleAbort = () => {
    abortRef.current = true;
    setAbortRequested(true);
    log('warn', 'Abort requested... finishing current sync');
  };

  // ============================================================================
  // FILTERING
  // ============================================================================

  const platforms = [...new Set(accounts.map(a => a.platform_provider || 'Unknown'))].sort();

  const getFilteredAccounts = useCallback(() => {
    let filtered = accounts;

    if (filterPlatform !== 'all') {
      filtered = filtered.filter(a => (a.platform_provider || 'Unknown') === filterPlatform);
    }

    if (filterStatus === 'never_synced') {
      filtered = filtered.filter(a => !a.last_sync_at);
    } else if (filterStatus === 'failed') {
      filtered = filtered.filter(a => a.last_sync_status === 'failed');
    } else if (filterStatus === 'no_data') {
      filtered = filtered.filter(a => !a.summary?.has_data);
    } else if (filterStatus === 'success') {
      filtered = filtered.filter(a => a.last_sync_status === 'success');
    }

    return filtered;
  }, [accounts, filterPlatform, filterStatus]);

  const filteredAccounts = getFilteredAccounts();

  // ============================================================================
  // STATS
  // ============================================================================

  const resultsArray = Array.from(results.values());
  const stats = {
    total: resultsArray.length,
    pending: resultsArray.filter(r => r.status === 'pending').length,
    syncing: resultsArray.filter(r => r.status === 'syncing').length,
    success: resultsArray.filter(r => r.status === 'success').length,
    failed: resultsArray.filter(r => r.status === 'failed').length,
    skipped: resultsArray.filter(r => r.status === 'skipped').length,
  };

  const progress = stats.total > 0 ? ((stats.success + stats.failed + stats.skipped) / stats.total) * 100 : 0;

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const getStatusBadge = (status: SyncResult['status']) => {
    const styles: Record<string, string> = {
      pending: 'bg-zinc-700 text-zinc-300',
      syncing: 'bg-blue-500/20 text-blue-400 animate-pulse',
      success: 'bg-emerald-500/20 text-emerald-400',
      failed: 'bg-red-500/20 text-red-400',
      skipped: 'bg-yellow-500/20 text-yellow-400',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getLogColor = (level: LogLevel) => {
    switch (level) {
      case 'success': return 'text-emerald-400';
      case 'error': return 'text-red-400';
      case 'warn': return 'text-yellow-400';
      case 'debug': return 'text-zinc-500';
      default: return 'text-zinc-300';
    }
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-6 space-y-6 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Sync All Accounts</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Sync all accounts for the current month with full debug output
          </p>
        </div>
        <div className="flex gap-3">
          {!syncing ? (
            <button
              onClick={syncAllAccounts}
              disabled={loading || filteredAccounts.length === 0}
              className={`px-5 py-2.5 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                forceResync ? 'bg-orange-600 hover:bg-orange-700' : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {forceResync ? `Force Resync ${filteredAccounts.length} Accounts` : `Sync ${filteredAccounts.length} Accounts`}
            </button>
          ) : (
            <button
              onClick={handleAbort}
              disabled={abortRequested}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg font-medium text-white disabled:opacity-50 transition-colors"
            >
              {abortRequested ? 'Aborting...' : 'Abort Sync'}
            </button>
          )}
          <button
            onClick={() => { setLogs([]); setResults(new Map()); }}
            disabled={syncing}
            className="px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg font-medium text-zinc-200 disabled:opacity-50 transition-colors"
          >
            Clear
          </button>
          <button
            onClick={loadAccounts}
            disabled={loading || syncing}
            className="px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg font-medium text-zinc-200 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Loading...' : 'Reload'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-[hsl(225,15%,10%)] rounded-lg p-4 border border-[hsl(225,15%,15%)]">
        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">Platform</label>
          <select
            value={filterPlatform}
            onChange={e => setFilterPlatform(e.target.value)}
            disabled={syncing}
            className="bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-1.5 text-sm focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="all">All Platforms ({accounts.length})</option>
            {platforms.map(p => (
              <option key={p} value={p}>
                {p} ({accounts.filter(a => (a.platform_provider || 'Unknown') === p).length})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">Account Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            disabled={syncing}
            className="bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-1.5 text-sm focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="all">All Statuses</option>
            <option value="success">Last Sync: Success ({accounts.filter(a => a.last_sync_status === 'success').length})</option>
            <option value="failed">Last Sync: Failed ({accounts.filter(a => a.last_sync_status === 'failed').length})</option>
            <option value="never_synced">Never Synced ({accounts.filter(a => !a.last_sync_at).length})</option>
            <option value="no_data">No Data ({accounts.filter(a => !a.summary?.has_data).length})</option>
          </select>
        </div>
        <div className="flex items-center gap-2 mt-5">
          <input
            type="checkbox"
            id="forceResync"
            checked={forceResync}
            onChange={e => setForceResync(e.target.checked)}
            disabled={syncing}
            className="rounded bg-zinc-800 border-zinc-600 text-orange-500 focus:ring-orange-500"
          />
          <label htmlFor="forceResync" className="text-sm text-zinc-400">
            Force resync <span className="text-orange-400">(all historical data)</span>
          </label>
        </div>
        <div className="flex items-center gap-2 mt-5">
          <input
            type="checkbox"
            id="showErrors"
            checked={showOnlyErrors}
            onChange={e => setShowOnlyErrors(e.target.checked)}
            className="rounded bg-zinc-800 border-zinc-600 text-emerald-500 focus:ring-emerald-500"
          />
          <label htmlFor="showErrors" className="text-sm text-zinc-400">Show only errors in results</label>
        </div>
        <div className="ml-auto text-right mt-5">
          <span className="text-sm text-zinc-400">
            {filteredAccounts.length} accounts selected
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      {syncing && (
        <div className="bg-[hsl(225,15%,10%)] rounded-lg p-4 border border-[hsl(225,15%,15%)]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex gap-4 text-sm">
              <span className="text-emerald-400">Success: {stats.success}</span>
              <span className="text-red-400">Failed: {stats.failed}</span>
              <span className="text-blue-400 animate-pulse">Syncing: {stats.syncing}</span>
              <span className="text-zinc-400">Pending: {stats.pending}</span>
            </div>
            <span className="text-sm text-zinc-400">
              {formatDuration(elapsed)} elapsed
            </span>
          </div>
          <div className="w-full bg-zinc-800 rounded-full h-3 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-emerald-600 to-emerald-400"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-right text-xs text-zinc-500 mt-1">
            {stats.success + stats.failed + stats.skipped} / {stats.total} ({Math.round(progress)}%)
          </div>
        </div>
      )}

      {/* Summary Cards (shown after sync) */}
      {stats.total > 0 && !syncing && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-[hsl(225,15%,10%)] rounded-lg p-4 border border-[hsl(225,15%,15%)]">
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Total</div>
            <div className="text-2xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="bg-[hsl(225,15%,10%)] rounded-lg p-4 border border-emerald-500/20">
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Success</div>
            <div className="text-2xl font-bold text-emerald-400">{stats.success}</div>
          </div>
          <div className="bg-[hsl(225,15%,10%)] rounded-lg p-4 border border-red-500/20">
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Failed</div>
            <div className="text-2xl font-bold text-red-400">{stats.failed}</div>
          </div>
          <div className="bg-[hsl(225,15%,10%)] rounded-lg p-4 border border-yellow-500/20">
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Skipped</div>
            <div className="text-2xl font-bold text-yellow-400">{stats.skipped}</div>
          </div>
          <div className="bg-[hsl(225,15%,10%)] rounded-lg p-4 border border-[hsl(225,15%,15%)]">
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Duration</div>
            <div className="text-2xl font-bold text-zinc-300">{formatDuration(elapsed)}</div>
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="bg-[hsl(225,15%,10%)] rounded-lg border border-[hsl(225,15%,15%)] overflow-hidden">
        <div className="p-4 border-b border-[hsl(225,15%,15%)] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {results.size > 0 ? 'Sync Results' : 'Accounts'} ({filteredAccounts.length})
          </h2>
          {results.size > 0 && stats.failed > 0 && (
            <span className="text-sm text-red-400 font-medium">
              {stats.failed} failed - scroll down for debug log
            </span>
          )}
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(225,15%,8%)] sticky top-0 z-10">
              <tr className="text-zinc-400 text-xs uppercase tracking-wider">
                <th className="p-3 text-left">#</th>
                <th className="p-3 text-left">Account</th>
                <th className="p-3 text-left">Platform</th>
                <th className="p-3 text-left">Network</th>
                <th className="p-3 text-left">Last Sync</th>
                <th className="p-3 text-left">Prev Status</th>
                <th className="p-3 text-center">Months</th>
                <th className="p-3 text-left">Sync Result</th>
                <th className="p-3 text-right">Duration</th>
                <th className="p-3 text-left">Error</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account, i) => {
                const result = results.get(account.id);
                if (showOnlyErrors && result && result.status !== 'failed') return null;

                return (
                  <tr
                    key={account.id}
                    className={`border-b border-[hsl(225,15%,13%)] hover:bg-[hsl(225,15%,12%)] transition-colors ${
                      result?.status === 'syncing' ? 'bg-blue-500/5' : ''
                    } ${result?.status === 'failed' ? 'bg-red-500/5' : ''}`}
                  >
                    <td className="p-3 text-zinc-500 font-mono text-xs">{i + 1}</td>
                    <td className="p-3">
                      <div className="font-medium text-white">{account.account_name}</div>
                      <div className="text-xs text-zinc-500">{account.username}</div>
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-zinc-800 rounded text-xs text-zinc-300">
                        {account.platform_provider || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-zinc-400 max-w-[150px] truncate">
                      {account.network?.name || '-'}
                    </td>
                    <td className="p-3 text-xs text-zinc-500 font-mono">
                      {account.last_sync_at
                        ? new Date(account.last_sync_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : <span className="text-yellow-500">Never</span>
                      }
                    </td>
                    <td className="p-3">
                      {account.last_sync_status ? (
                        <span className={`text-xs ${
                          account.last_sync_status === 'success' ? 'text-emerald-400' :
                          account.last_sync_status === 'failed' ? 'text-red-400' :
                          'text-zinc-400'
                        }`}>
                          {account.last_sync_status}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-600">-</span>
                      )}
                    </td>
                    <td className="p-3 text-center text-xs">
                      {account.summary?.months_synced ?? 0}
                    </td>
                    <td className="p-3">
                      {result ? getStatusBadge(result.status) : (
                        <span className="text-xs text-zinc-600">-</span>
                      )}
                    </td>
                    <td className="p-3 text-right text-xs font-mono text-zinc-400">
                      {result?.duration ? `${(result.duration / 1000).toFixed(1)}s` : '-'}
                    </td>
                    <td className="p-3 text-xs text-red-400 max-w-[250px] truncate" title={result?.error}>
                      {result?.error || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Debug Log */}
      <div className="bg-[hsl(225,15%,10%)] rounded-lg border border-[hsl(225,15%,15%)] overflow-hidden">
        <div className="p-4 border-b border-[hsl(225,15%,15%)] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Debug Log ({logs.length} entries)</h2>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const text = logs.map(l => `[${l.timestamp}] [${l.level.toUpperCase()}] ${l.accountName ? `(${l.accountName}) ` : ''}${l.message}`).join('\n');
                navigator.clipboard.writeText(text);
              }}
              className="px-3 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-xs text-zinc-300 transition-colors"
            >
              Copy Log
            </button>
            <button
              onClick={() => {
                // Export failed accounts as JSON
                const failed = resultsArray.filter(r => r.status === 'failed');
                const text = JSON.stringify(failed, null, 2);
                navigator.clipboard.writeText(text);
              }}
              className="px-3 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-xs text-zinc-300 transition-colors"
              disabled={stats.failed === 0}
            >
              Copy Failed ({stats.failed})
            </button>
          </div>
        </div>
        <div
          ref={logContainerRef}
          className="font-mono text-xs max-h-[400px] overflow-y-auto p-4 space-y-0.5"
        >
          {logs.length === 0 ? (
            <div className="text-zinc-600">No log entries yet. Click "Sync All" to start.</div>
          ) : (
            logs.map((entry, i) => (
              <div key={i} className={`${getLogColor(entry.level)} leading-relaxed`}>
                <span className="text-zinc-600">[{entry.timestamp}]</span>{' '}
                <span className={`font-semibold ${getLogColor(entry.level)}`}>
                  [{entry.level.toUpperCase().padEnd(5)}]
                </span>{' '}
                {entry.accountName && (
                  <span className="text-zinc-500">({entry.accountName}) </span>
                )}
                {entry.message}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Failed Accounts Detail (shown after sync) */}
      {stats.failed > 0 && !syncing && (
        <div className="bg-[hsl(225,15%,10%)] rounded-lg border border-red-500/20 overflow-hidden">
          <div className="p-4 border-b border-red-500/20">
            <h2 className="text-lg font-semibold text-red-400">Failed Accounts Detail ({stats.failed})</h2>
          </div>
          <div className="p-4 space-y-3">
            {resultsArray
              .filter(r => r.status === 'failed')
              .map(r => (
                <div key={r.accountId} className="bg-red-500/5 rounded-lg p-3 border border-red-500/10">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-medium text-white">{r.accountName}</span>
                      <span className="ml-2 text-xs text-zinc-500">{r.platform}</span>
                    </div>
                    <span className="text-xs text-zinc-500">{r.duration ? `${(r.duration / 1000).toFixed(1)}s` : ''}</span>
                  </div>
                  <div className="text-sm text-red-400 mb-2">{r.error}</div>
                  {r.response && (
                    <details className="text-xs">
                      <summary className="text-zinc-500 cursor-pointer hover:text-zinc-300">Raw Response</summary>
                      <pre className="mt-2 bg-[hsl(225,15%,6%)] rounded p-2 overflow-x-auto text-zinc-400 max-h-40 overflow-y-auto">
                        {JSON.stringify(r.response, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
